import DateFnsUtils from "@date-io/date-fns";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import EditIcon from "@material-ui/icons/Edit";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import esLocale from "date-fns/locale/es";
import { Form, Formik } from "formik";
import moment from "moment";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import * as Yup from "yup";
import { apiSetServiceDelay } from "../../../../../../api/service/apiSetServiceDelay";
import { FormikTextField } from "../../../../../../components/FormikFields";
import FormikDateTimePicker from "../../../../../../components/FormikFields/FormikDateTimePicker";
import useSnacks from "../../../../../../components/hooks/useSnacks";

const StyledDialog = styled(Dialog)``;
const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .MuiSvgIcon-root {
    vertical-align: bottom;
    margin-right: 0.5rem;
  }
`;
const StyledInfo = styled(Card)`
  border: 0.0625rem solid rgba(102, 60, 0, 0.15);
  box-shadow: none;
  border-radius: 0.375rem;
  transition: all ease-in-out 150ms;
  background-color: rgb(255, 244, 229);
  display: flex;
  flex-direction: row;
  justify-content: center;

  &.outdated {
    background-color: #fdecea;
  }
`;
const StyledInfoContent = styled(CardContent)`
  text-align: center;
  padding: 0.25rem;
  &:last-child {
    padding: 0.25rem;
  }
`;
const StyledCardActions = styled(CardActions)`
  flex-direction: row;
  justify-content: center;
  text-decoration: none;
`;
const StyledInfoTitle = styled(Typography)`
  font-size: 0.75rem;
  line-height: 1.25;
  color: #002740;
`;

const StyledInfoDelayDate = styled(Typography)`
  font-size: 0.75rem;
  color: #002740;
  line-height: 1.25;
  &.outdated {
    text-decoration: line-through;
  }
`;
const StyledEditButton = styled(Button)`
  font-size: 0.75rem;
  line-height: 1.25;
  text-transform: none;
  padding: 0.25rem 0.75rem;
  min-width: 4rem;
  background-color: rgba(9, 30, 66, 0.07);
  transition: background-color 0.1s ease-out 0s, box-shadow 0.15s ease-out 0s;

  &:hover,
  &:focus {
    background-color: rgba(9, 30, 66, 0.12);
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.09);
  }
`;
const StyledDelayButton = styled(Button)`
  font-weight: 700;
  padding: 0.25rem 0.375rem;
`;

interface IDelayButton {
  serviceId: string;
  serviceName: string | null;
  delayDate: string | null;
  delayReason: string | null;
  onDelaySet: () => void;
}

interface IDelayRequest {
  delayDate: string;
  delayReason: string;
}

const DelayButton = (props: IDelayButton) => {
  const { serviceId, serviceName, onDelaySet, delayDate, delayReason } = props;
  const now = moment();
  const defaultDelayedDate = moment().set({
    h: now.hour(),
    m: now.minute() + 30,
  });
  const delayDateMoment = delayDate ? moment(delayDate) : null;
  const delayedDateValue = delayDate ? moment(delayDate) : defaultDelayedDate;
  const initialValues: IDelayRequest = {
    delayDate: delayedDateValue.toLocaleString(),
    delayReason: delayReason ?? "",
  };

  const { snackSuccessMessage } = useSnacks();

  const { formatMessage } = useIntl();

  const [state, setState] = useState({
    inProcess: false as boolean,
    openDialog: false as boolean,
    delayDate: null as null | string,
    delayReason: null as null | string,
  });

  const setOpen = (open: boolean) => {
    setState({
      ...state,
      openDialog: open,
    });
  };

  const setProcess = (start: boolean) => {
    setState({
      ...state,
      inProcess: start,
    });
  };

  const handleCancelDialog = () => {
    setOpen(false);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const validationSchema = Yup.object({
    delayReason: Yup.string().required(
      formatMessage({
        id: "DelayButton.Debes introducir un motivo",
        defaultMessage: "Debes introducir un motivo",
      })
    ),
    delayDate: Yup.mixed()
      .test(
        "valid_date",
        formatMessage({
          id: "DelayButton.La fecha tiene que ser válida",
          defaultMessage: "La fecha tiene que ser válida",
        }),
        (value) => {
          const momDate = moment(value);
          if (momDate.isValid()) {
            return true;
          }
          return false;
        }
      )
      .test(
        "valid_date_future",
        formatMessage({
          id: "DelayButton.La fecha debe ser futura",
          defaultMessage: "La fecha debe ser futura",
        }),
        (value) => {
          const now = moment();
          const momDate = moment(value);
          if (momDate.isBefore(now)) {
            return false;
          }
          return true;
        }
      ),
  });

  const handleSubmitForm = async (values: IDelayRequest, actions: any) => {
    const delayDate = moment(values.delayDate);
    setProcess(true);
    await apiSetServiceDelay(serviceId, {
      delayedDate: delayDate.format(),
      delayedReason: values.delayReason,
    });
    onDelaySet();
    setOpen(false);
    snackSuccessMessage(
      formatMessage({
        id: "DelayButton.Servicio aplazado",
        defaultMessage: "Servicio aplazado",
      })
    );
  };

  const delayButtonAction = (
    <StyledDelayButton
      color="primary"
      variant="outlined"
      onClick={handleOpenDialog}
      fullWidth
      startIcon={<AccessAlarmIcon />}
    >
      <FormattedMessage id="DelayButton.Posponer" defaultMessage="Posponer" />
    </StyledDelayButton>
  );

  const delayedInfo = (
    <StyledInfo
      className={
        delayDateMoment && delayDateMoment.isBefore(now)
          ? "outdated"
          : undefined
      }
    >
      <Tooltip title={delayReason ?? ""} placement="right-start">
        <StyledInfoContent>
          <StyledInfoTitle>
            <FormattedMessage
              id="DelayButton.Aplazado hasta"
              defaultMessage="Aplazado hasta"
            />
          </StyledInfoTitle>
          <StyledInfoDelayDate
            className={
              delayDateMoment && delayDateMoment.isBefore(now)
                ? "outdated"
                : undefined
            }
          >
            {delayDateMoment?.format("DD/MM/YYYY HH:mm")}
          </StyledInfoDelayDate>
          <StyledCardActions disableSpacing>
            <StyledEditButton
              startIcon={<EditIcon />}
              variant="contained"
              onClick={handleOpenDialog}
            >
              <FormattedMessage
                id="DelayButton.Editar"
                defaultMessage="Editar"
              />
            </StyledEditButton>
          </StyledCardActions>
        </StyledInfoContent>
      </Tooltip>
    </StyledInfo>
  );

  return (
    <>
      {null === delayDate ? delayButtonAction : delayedInfo}
      <StyledDialog open={state.openDialog}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
        >
          <Form>
            <StyledDialogTitle>
              <AccessAlarmIcon />
              <FormattedMessage
                id="DelayButton.Posponer valoración de {serviceName}"
                defaultMessage="Posponer valoración de {serviceName}"
                values={{ serviceName: serviceName }}
              />
            </StyledDialogTitle>
            <DialogContent>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
                <FormikDateTimePicker
                  name="delayDate"
                  label={formatMessage({
                    id: "DelayButton.Posponer hasta:",
                    defaultMessage: "Posponer hasta:",
                  })}
                />
              </MuiPickersUtilsProvider>
              <FormikTextField
                multiline={true}
                size="small"
                fullWidth
                name="delayReason"
                required
                minRows="5"
                label={formatMessage({
                  id: "DelayButton.Razón del aplazamiento:",
                  defaultMessage: "Razón del aplazamiento:",
                })}
              />
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancelDialog}
                disabled={state.inProcess}
              >
                <FormattedMessage
                  id="DelayButton.Cancelar"
                  defaultMessage="Cancelar"
                />
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={state.inProcess}
              >
                <FormattedMessage
                  id="DelayButton.Posponer"
                  defaultMessage="Posponer"
                />
              </Button>
            </DialogActions>
          </Form>
        </Formik>
      </StyledDialog>
    </>
  );
};

export default DelayButton;
